import axios from 'axios';
import { getApiBaseUrl } from '../lib/config';
import { saveIntendedRedirect } from '../lib/intendedRedirect';

const api = axios.create({
    timeout: 30000,
    headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
    },
    withCredentials: false,
});

/** Paths that never require auth — 401 must not bounce visitors to login. */
function isPublicWebsitePath(pathname) {
    if (!pathname) return false;
    if (pathname === '/') return true;
    const publicPrefixes = [
        '/about',
        '/vision',
        '/events',
        '/ministries',
        '/testimonies',
        '/contact',
        '/news',
        '/messages',
        '/login',
    ];
    return publicPrefixes.some(
        (p) => pathname === p || pathname.startsWith(`${p}/`)
    );
}

api.interceptors.request.use(
    (config) => {
        config.baseURL = getApiBaseUrl();
        config.headers['X-Client-Platform'] = 'web';

        if (config.data instanceof FormData) {
            delete config.headers['Content-Type'];
        }

        const token = localStorage.getItem('auth_token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => Promise.reject(error)
);

api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            const url = String(error.config?.url || '');
            const method = String(error.config?.method || 'get').toLowerCase();
            const isAuthEndpoint =
                url.includes('/login') ||
                url.includes('/logout') ||
                url.includes('/me');

            const isPublicLiveRead =
                method === 'get' &&
                (url === '/live' ||
                    url.endsWith('/live') ||
                    url.includes('/watch') ||
                    url.includes('/live-programs/public') ||
                    url.includes('/public/updates') ||
                    url.includes('/public/messages') ||
                    url.includes('/public/ecards') ||
                    url.includes('/public/contact') ||
                    (url.includes('/live-programs/') && url.includes('/comments')));

            localStorage.removeItem('auth_token');
            localStorage.removeItem('user');

            const path = window.location.pathname || '/';
            // Stay on public marketing pages; only force login for portal / live.
            if (
                !isAuthEndpoint &&
                !isPublicLiveRead &&
                !isPublicWebsitePath(path) &&
                !path.startsWith('/login') &&
                !path.startsWith('/portal') &&
                !path.startsWith('/admin/login')
            ) {
                const redirect = `${path}${window.location.search || ''}`;
                saveIntendedRedirect(redirect);
                const loginBase =
                    path === '/admin' || path.startsWith('/admin/')
                        ? '/portal'
                        : '/login';
                window.location.href = `${loginBase}?redirect=${encodeURIComponent(redirect)}`;
            }
        }
        return Promise.reject(error);
    }
);

export default api;
